import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { AVAILABLE_AREAS } from '../team-members/constants';
import { GET_DASHBOARD_TEAM_STATUS } from '../../graphql/team-availability-queries';
import {
  toCalendarDateKey,
  toLocalCalendarDate,
} from '../../utils/availabilityDateUtils';
import { useMyPrivileges } from '../../hooks/useMyPrivileges';

const STATUS_STYLES = {
  OFF: {
    label: 'Off',
    badge: 'border-gray-200 bg-gray-100 text-gray-700',
    dot: 'bg-gray-500',
  },
  SICK: {
    label: 'Sick',
    badge: 'border-rose-200 bg-rose-50 text-rose-700',
    dot: 'bg-rose-500',
  },
  PTO: {
    label: 'PTO',
    badge: 'border-violet-200 bg-violet-50 text-violet-800',
    dot: 'bg-violet-500',
  },
};

const AREA_LABELS = Object.fromEntries(AVAILABLE_AREAS.map((area) => [area.value, area.label]));

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = toLocalCalendarDate(startDate);
  const end = toLocalCalendarDate(endDate);
  if (toCalendarDateKey(start) === toCalendarDateKey(end)) {
    return format(start, 'MMM d, yyyy');
  }
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
};

function TeamAvailabilityStatusWidget({ user, isLoggingOut = false }) {
  const { hasPrivilege, loading: privilegesLoading } = useMyPrivileges();
  const canShowWidget = hasPrivilege('TEAM_AVAILABILITY');
  const userAreas = useMemo(
    () => (Array.isArray(user?.areas) ? user.areas.filter(Boolean) : []),
    [user?.areas],
  );
  const areaOptions = useMemo(
    () => userAreas.length > 0
      ? AVAILABLE_AREAS.filter((area) => userAreas.includes(area.value))
      : AVAILABLE_AREAS,
    [userAreas],
  );
  const initialArea = userAreas.length === 1 ? userAreas[0] : 'ALL';
  const [selectedArea, setSelectedArea] = useState(initialArea);

  useEffect(() => {
    setSelectedArea(userAreas.length === 1 ? userAreas[0] : 'ALL');
  }, [userAreas]);

  const today = new Date();
  const todayKey = format(today, 'yyyy-MM-dd');
  const { data, loading, error } = useQuery(GET_DASHBOARD_TEAM_STATUS, {
    variables: {
      date: `${todayKey}T00:00:00.000Z`,
      ...(selectedArea !== 'ALL' ? { area: selectedArea } : {}),
    },
    skip: !user || isLoggingOut || !canShowWidget,
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  });

  const teamMembers = data?.dashboardTeamStatus || [];
  const allAreasLabel = userAreas.length > 0 ? 'All My Areas' : 'All Areas';

  if (privilegesLoading || !canShowWidget) return null;

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Team Status</h2>
              {!loading && (
                <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800">
                  {teamMembers.length}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">Off today · {format(today, 'EEEE, MMMM d')}</p>
          </div>

          <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
            <span>Area</span>
            <select
              value={selectedArea}
              onChange={(event) => setSelectedArea(event.target.value)}
              className="rounded-md border border-gray-300 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              {(userAreas.length !== 1) && <option value="ALL">{allAreasLabel}</option>}
              {areaOptions.map((area) => (
                <option key={area.value} value={area.value}>{area.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">Loading team status...</div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-sm text-red-600">Unable to load team status.</div>
        ) : teamMembers.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="text-sm font-medium text-gray-700">No team members are marked off today</div>
            <div className="mt-1 text-xs text-gray-500">No eligible off, sick, or PTO entries were found.</div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {teamMembers.map((member) => {
              const status = STATUS_STYLES[member.status] || STATUS_STYLES.OFF;
              const dateRange = formatDateRange(member.startDate, member.endDate);
              const areaLabel = (member.employeeAreas || [])
                .map((area) => AREA_LABELS[area] || area)
                .join(', ');
              const ptoRemaining = member.status === 'PTO'
                ? member.daysRemaining == null
                  ? null
                  : `${member.daysRemaining} ${member.daysRemaining === 1 ? 'day' : 'days'} remaining`
                : null;

              return (
                <li key={member.employeeId} className="px-4 py-3 sm:px-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${status.dot}`} />
                        <span className="truncate text-sm font-semibold text-gray-900">
                          {member.employeeName}
                        </span>
                      </div>
                      <div className="ml-[18px] mt-1 text-xs text-gray-500">
                        {areaLabel || 'No area set'}
                      </div>
                      <div className="ml-[18px] mt-1 text-xs text-gray-600">
                        {dateRange || `${status.label} today`}
                        {ptoRemaining && ` · ${ptoRemaining}`}
                      </div>
                    </div>
                    <span className={`inline-flex flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${status.badge}`}>
                      {status.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default TeamAvailabilityStatusWidget;
